import React, { useState, useEffect, useCallback } from 'react';
import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XMarkIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import {
  listArchivedTaskTemplates,
  restoreTaskTemplate,
  hardDeleteTaskTemplate,
  getTemplateDependencies,
  formatJurisdictionLabel,
  ARCHIVE_REASON_LABELS,
  ARCHIVE_REASON_COLORS,
  type TaskTemplateListItem,
  type TemplateDependencyCheckResult,
} from './templateManagementService';

interface ArchivedTemplatesProps {
  onViewTemplate?: (templateId: string) => void;
}

interface ToastItem {
  id: number;
  type: 'success' | 'error';
  message: string;
}

let toastCounter = 0;

const ArchivedTemplates: React.FC<ArchivedTemplatesProps> = ({ onViewTemplate }) => {
  const [templates, setTemplates] = useState<TaskTemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterReason, setFilterReason] = useState('');
  const [page, setPage] = useState(1);

  // Restore state
  const [restoreTarget, setRestoreTarget] = useState<TaskTemplateListItem | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);

  // Hard delete state
  const [deleteTarget, setDeleteTarget] = useState<TaskTemplateListItem | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDeps, setDeleteDeps] = useState<TemplateDependencyCheckResult | null>(null);
  const [loadingDeleteDeps, setLoadingDeleteDeps] = useState(false);

  // Detail view
  const [detailTemplate, setDetailTemplate] = useState<TaskTemplateListItem | null>(null);

  // Toasts
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = ++toastCounter;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 5000);
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const result = await listArchivedTaskTemplates({
        search: searchQuery || undefined,
        archive_reason: filterReason || undefined,
        page,
        pageSize: 20,
      });
      setTemplates(result.templates);
      setTotal(result.pagination.total);
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to load archived templates');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterReason, page, addToast]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleRestore = async () => {
    if (!restoreTarget) return;
    try {
      setIsRestoring(true);
      await restoreTaskTemplate(restoreTarget.id);
      addToast('success', `Template "${restoreTarget.name}" restored successfully.`);
      setRestoreTarget(null);
      loadTemplates();
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to restore template');
    } finally {
      setIsRestoring(false);
    }
  };

  const openDeleteConfirm = async (template: TaskTemplateListItem) => {
    setDeleteTarget(template);
    setDeleteConfirmName('');
    setDeleteDeps(null);
    try {
      setLoadingDeleteDeps(true);
      const deps = await getTemplateDependencies(template.id);
      setDeleteDeps(deps);
    } catch {
      // Non-fatal — proceed with delete dialog
    } finally {
      setLoadingDeleteDeps(false);
    }
  };

  const handleHardDelete = async () => {
    if (!deleteTarget || deleteConfirmName !== deleteTarget.name) return;
    try {
      setIsDeleting(true);
      await hardDeleteTaskTemplate(deleteTarget.id);
      addToast('success', `Template "${deleteTarget.name}" permanently deleted.`);
      setDeleteTarget(null);
      loadTemplates();
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to delete template');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return 'N/A';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <ArchiveBoxIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Archived Templates</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {loading ? 'Loading...' : `${total} archived template(s)`}
              </p>
            </div>
          </div>
          <button
            onClick={loadTemplates}
            disabled={loading}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
          >
            <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Search & Filter */}
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              placeholder="Search archived templates..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-lex-accent transition-colors"
            />
          </div>
          <select
            value={filterReason}
            onChange={(e) => { setFilterReason(e.target.value); setPage(1); }}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-lex-accent transition-colors"
          >
            <option value="">All Reasons</option>
            {Object.entries(ARCHIVE_REASON_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* List */}
      <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
        {loading && templates.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-lex-accent border-t-transparent" />
          </div>
        )}

        {!loading && templates.length === 0 && (
          <div className="text-center py-12">
            <ArchiveBoxIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No archived templates found</p>
          </div>
        )}

        {templates.map((template) => (
          <div key={template.id} className="px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors duration-150">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{template.name}</h4>
                  {template.archive_reason && (
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ARCHIVE_REASON_COLORS[template.archive_reason] || ARCHIVE_REASON_COLORS.other}`}>
                      {ARCHIVE_REASON_LABELS[template.archive_reason] || template.archive_reason}
                    </span>
                  )}
                  {template.template_kind && template.template_kind !== 'base' && (
                    <span className="inline-flex items-center rounded-full bg-lex-accent-soft dark:bg-lex-accent-soft px-2 py-0.5 text-xs font-medium text-lex-accent dark:text-lex-accent">
                      {template.template_kind.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
                <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                  <div>
                    <span className="text-gray-400 dark:text-gray-500">Practice Area: </span>
                    {template.practice_area || 'Unspecified'}
                  </div>
                  <div>
                    <span className="text-gray-400 dark:text-gray-500">Jurisdiction: </span>
                    {formatJurisdictionLabel(template.jurisdiction)}
                  </div>
                  <div>
                    <span className="text-gray-400 dark:text-gray-500">Archived: </span>
                    {formatDate(template.archived_date)}
                  </div>
                  <div>
                    <span className="text-gray-400 dark:text-gray-500">Usage: </span>
                    {template.usage_count} task(s)
                  </div>
                </div>
                {template.archive_notes && (
                  <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500 italic truncate max-w-md">
                    {template.archive_notes}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setDetailTemplate(template)}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
                >
                  <EyeIcon className="h-3.5 w-3.5" />
                  View
                </button>
                <button
                  onClick={() => setRestoreTarget(template)}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors duration-150"
                >
                  <ArrowPathIcon className="h-3.5 w-3.5" />
                  Restore
                </button>
                <button
                  onClick={() => openDeleteConfirm(template)}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-150"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {total > 20 && (
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">
            Page {page} of {Math.ceil(total / 20)}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= Math.ceil(total / 20)}
              className="px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ─── Restore Confirmation Modal ─── */}
      {restoreTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 dark:bg-black/70" onClick={() => !isRestoring && setRestoreTarget(null)} />
          <div className="relative z-10 w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex flex-col items-center text-center">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
                <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Restore Archived Template</h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                Are you sure you want to restore <span className="font-medium">&quot;{restoreTarget.name}&quot;</span>?
                It will be available for use in matters again.
              </p>
            </div>
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={() => setRestoreTarget(null)}
                disabled={isRestoring}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
              >
                Cancel
              </button>
              <button
                onClick={handleRestore}
                disabled={isRestoring}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors duration-150"
              >
                {isRestoring ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Restoring...
                  </>
                ) : (
                  'Restore Template'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Hard Delete Confirmation Modal ─── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 dark:bg-black/70" onClick={() => !isDeleting && setDeleteTarget(null)} />
          <div className="relative z-10 w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex flex-col items-center text-center">
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Permanently Delete Template</h3>

              {loadingDeleteDeps && (
                <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-lex-accent border-t-transparent" />
                  Checking references...
                </div>
              )}

              {deleteDeps && !deleteDeps.can_hard_delete && (
                <div className="mt-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300 text-left w-full">
                  Cannot permanently delete — tasks reference this template.
                  Remove or reassign all tasks created from this template first.
                </div>
              )}

              {(!deleteDeps || deleteDeps.can_hard_delete) && !loadingDeleteDeps && (
                <>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                    This action is <span className="font-semibold text-red-600 dark:text-red-400">permanent</span> and cannot be undone.
                    Type the template name to confirm.
                  </p>
                  <input
                    type="text"
                    value={deleteConfirmName}
                    onChange={(e) => setDeleteConfirmName(e.target.value)}
                    placeholder={deleteTarget.name}
                    className="mt-3 w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                  />
                </>
              )}
            </div>
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={isDeleting}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
              >
                Cancel
              </button>
              <button
                onClick={handleHardDelete}
                disabled={isDeleting || deleteConfirmName !== deleteTarget.name || (deleteDeps !== null && !deleteDeps.can_hard_delete)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="h-4 w-4" />
                    Delete Permanently
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Detail View Modal ─── */}
      {detailTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 dark:bg-black/70" onClick={() => setDetailTemplate(null)} />
          <div className="relative z-10 w-full max-w-lg mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Archived Template Details</h3>
              <button
                onClick={() => setDetailTemplate(null)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-3 max-h-[60vh] overflow-y-auto">
              <DetailRow label="Name" value={detailTemplate.name} />
              <DetailRow label="Practice Area" value={detailTemplate.practice_area || 'Unspecified'} />
              <DetailRow label="Jurisdiction" value={formatJurisdictionLabel(detailTemplate.jurisdiction)} />
              <DetailRow label="Case Subtype" value={detailTemplate.case_subtype?.replace(/_/g, ' ') || 'N/A'} />
              <DetailRow label="Template Kind" value={detailTemplate.template_kind?.replace(/_/g, ' ') || 'base'} />
              <DetailRow label="Tasks / Phases" value={`${detailTemplate.task_count} / ${detailTemplate.phase_count}`} />
              <DetailRow label="Usage Count" value={String(detailTemplate.usage_count)} />
              <DetailRow
                label="Archive Reason"
                value={
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ARCHIVE_REASON_COLORS[detailTemplate.archive_reason || ''] || ARCHIVE_REASON_COLORS.other}`}>
                    {ARCHIVE_REASON_LABELS[detailTemplate.archive_reason || ''] || detailTemplate.archive_reason || 'N/A'}
                  </span>
                }
              />
              <DetailRow label="Archived Date" value={formatDate(detailTemplate.archived_date)} />
              {detailTemplate.archive_notes && (
                <DetailRow label="Notes" value={detailTemplate.archive_notes} />
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex justify-end">
              <button
                onClick={() => setDetailTemplate(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toasts */}
      <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium backdrop-blur-sm ${
              toast.type === 'success'
                ? 'bg-green-600/90 text-white'
                : 'bg-red-600/90 text-white'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5" />
            )}
            <span>{toast.message}</span>
            <button onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))} className="ml-2">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper component
const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-start gap-3">
    <span className="text-sm text-gray-500 dark:text-gray-400 w-32 flex-shrink-0">{label}</span>
    <span className="text-sm text-gray-900 dark:text-gray-100">{value}</span>
  </div>
);

export default ArchivedTemplates;
